import { useEffect, useState } from "react";
import { DimensionsAndWeight } from "../../utils";
import StandardModal from "@/components/StandardModal";
import IconBoxIso from "@/components/icons/IconBoxIso";
import DimensionsAndWeightRow from "../../DimensionsAndWeightRow/DimensionsAndWeightRow";
import InputNumber from "@/components/forms/InputNumber/InputNumber";
import Button from "@/components/forms/Button";
import InfoBanner from "@/components/InformationBanner/InfoBanner";
import { Table } from "@/components/Table/Table";
import IconCheck from "@/components/icons/IconCheck";
import IconDelete from "@/components/icons/iconDelete";

export type Pallet = {
  height: number;
  weight: number;
  isStackable: boolean;
};

interface Props {
  open: boolean;
  onClose: (value?: Pallet[]) => void;
  value?: Pallet[] | null;
}

export default function PalletInformationModal(props: Props) {
  const { open, onClose } = props;
  const [pallets, setPallets] = useState<Pallet[]>([]);
  const [dimWeight, setDimWeight] = useState<DimensionsAndWeight>({ height: 0, length: 0, width: 0, weight: 0});
  const [isStackable, setIsStackable] = useState(false);
  const [createPalletCount, setCreatePalletCount] = useState(1);

  useEffect(() => setPallets(props.value ?? []), [props.value]);

  const addPallets = () => {
    const newPallets: Pallet[] = Array.from({ length: createPalletCount }).map(() => ({
      weight: dimWeight.weight,
      height: dimWeight.height,
      isStackable,
    }));
    setPallets([...pallets, ...newPallets]);
  };
  const deletePallet = (index: number) => {
    pallets.splice(index, 1);
    setPallets([...pallets]);
  };

  return (
    <StandardModal
      icon={IconBoxIso}
      title="Pallet Information"
      submitText="Save"
      submitDisabledReason={pallets.length < 1 ? "Add pallets to continue" : null}
      open={open}
      onClose={onClose}
      onSubmit={() => onClose(pallets)}
      body={
        <div style={{display: "flex", gap: "12px", flexDirection: "column"}}>
          {pallets.length < 1 && <InfoBanner
            message="No pallets yet. At least one is required to continue. Add pallets below."
            type="info"
            storageKey="mustAddPalletsKey"
          />}
          <DimensionsAndWeightRow
            value={dimWeight}
            onChange={setDimWeight}
            palletStackable={isStackable}
            palletStackableOnChange={setIsStackable}
            oversizedControls={false}
            showMaxWeightAndDimensionsTooltip={true}
            disableLengthAndWidth={true}
            maxDimension={72}
            minWeight={150}
            maxWeight={1500}
          />
          <div style={{display: "flex", gap: "12px", justifyContent: "flex-end"}}>
            <InputNumber value={createPalletCount} onChange={setCreatePalletCount} style={{width: "104px"}} min={1} />
            <Button type="black" onClick={addPallets}>
              <span>Add Pallet<span style={{visibility: createPalletCount === 1 ? "hidden" : null}}>s</span> +</span>
            </Button>
          </div>
          {pallets?.length > 0 && <Table<Pallet>
            tableName="palletsTable"
            columnControls={false}
            data={pallets}
            columns={[
              {
                title: "#",
                id: "index",
                alignment: "left",
                width: "50px",
                renderRowCell: row => row.rowIndex + 1,
              },
              {
                title: "Dimensions (in)",
                id: "dimensions",
                renderRowCell: row => <span>40&times;48&times;{row.rowData.height} in</span>,
              },
              {
                title: "Weight (lbs)",
                id: "weight",
                renderRowCell: row => <span>{row.rowData.weight} lbs</span>,
              },
              {
                title: "Stackable",
                id: "stackable",
                renderRowCell: row => row.rowData.isStackable && <IconCheck />,
              },
              {
                title: "",
                id: "delete",
                renderRowCell: row => (
                  <Button type="text" onClick={() => deletePallet(row.rowIndex)}>
                    <IconDelete size={24} color="var(--static-text-standard)" />
                  </Button>
                ),
              },
            ]}
          />}
        </div>
      }
    />
  )
}
